/**
 * Steps Panel Component
 * 
 * Left panel showing list of form steps with drag-and-drop reordering.
 */

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Plus,
  Trash2,
  ChevronRight,
  Layers,
} from 'lucide-react';
import { useFormBuilder } from '../../contexts';
import type { FormStep } from '@formflow/shared';
import './StepsPanel.css';

// =============================================================================
// Sortable Step Item
// =============================================================================

interface SortableStepItemProps {
  step: FormStep;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  stepNumber: number;
}

function SortableStepItem({
  step,
  isSelected,
  onSelect,
  onDelete,
  stepNumber,
}: SortableStepItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${step.name}"?`)) {
      onDelete();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`step-item ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <button
        className="drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </button>

      <div className="step-content">
        <div className="step-number">{stepNumber}</div>
        <div className="step-info">
          <span className="step-name">{step.name}</span>
          <span className="step-meta">
            {step.variants.length > 1 && (
              <span className="variant-badge">{step.variants.length} variants</span>
            )}
            {step.variants[0]?.fields.length || 0} fields
          </span>
        </div>
      </div>

      <div className="step-actions">
        <button
          className="btn-icon-sm"
          onClick={handleDelete}
          aria-label="Delete step"
        >
          <Trash2 size={14} />
        </button>
        <ChevronRight size={16} className="chevron" />
      </div>
    </div>
  );
}

// =============================================================================
// Steps Panel
// =============================================================================

export function StepsPanel() {
  const { state, selectStep, addStep, deleteStep, reorderSteps } = useFormBuilder();
  const { form, selectedStepId } = state;
  const [isAdding, setIsAdding] = useState(false);
  const [newStepName, setNewStepName] = useState('');

  const steps = form?.active_version?.steps || [];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(steps, oldIndex, newIndex).map((s) => s.id);
      await reorderSteps(newOrder);
    }
  };

  const handleAddStep = async () => {
    if (!newStepName.trim()) return;

    try {
      await addStep({ name: newStepName.trim() });
      setNewStepName('');
      setIsAdding(false);
    } catch (error) {
      console.error('Failed to add step:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddStep();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewStepName('');
    }
  };

  return (
    <aside className="steps-panel">
      <div className="panel-header">
        <h2 className="panel-title">
          <Layers size={18} />
          Steps
        </h2>
        <button
          className="btn-icon-sm btn-add"
          onClick={() => setIsAdding(true)}
          aria-label="Add step"
        >
          <Plus size={16} />
        </button>
      </div>

      <div className="steps-list">
        {steps.length === 0 ? (
          <div className="empty-state">
            <p>No steps yet</p>
            <button className="btn btn-sm btn-primary" onClick={() => setIsAdding(true)}>
              <Plus size={14} />
              Add First Step
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={steps.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {steps.map((step, index) => (
                <SortableStepItem
                  key={step.id}
                  step={step}
                  stepNumber={index + 1}
                  isSelected={selectedStepId === step.id}
                  onSelect={() => selectStep(step.id)}
                  onDelete={() => deleteStep(step.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {isAdding && (
          <div className="add-step-form">
            <input
              type="text"
              className="step-name-input"
              placeholder="Step name..."
              value={newStepName}
              onChange={(e) => setNewStepName(e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
            />
            <div className="add-step-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleAddStep}
                disabled={!newStepName.trim()}
              >
                Add
              </button>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setIsAdding(false);
                  setNewStepName('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="panel-footer">
        <button
          className="btn btn-sm btn-ghost btn-full"
          onClick={() => setIsAdding(true)}
        >
          <Plus size={14} />
          Add Step
        </button>
      </div>
    </aside>
  );
}

export default StepsPanel;
